import { AxiosError } from 'axios';
import { StatusCode } from '@novaheal/types';
import {
  isTranslatorRelatedError,
  resolveAxiosUserMessage,
  resolveRuntimeUserMessage,
  resolveTranslatorUserMessage,
  type UserFacingError,
} from 'utils/errorMessages';

export type ErrorLayer = 'fetch' | 'runtime' | 'action';
export type ErrorExpectation = 'expected' | 'unexpected';

export type ClassifiedError = {
  layer: ErrorLayer;
  expectation: ErrorExpectation;
  userMessage: UserFacingError;
  codeError?: string;
  httpStatus?: number;
  shouldReport: boolean;
};

export type ClassifyErrorOptions = {
  layer?: ErrorLayer;
  expectedCodeErrors?: string[];
};

/**
 * Single source of truth for known backend `codeError` values.
 * Add a new code once here — copy and expected-by-default behavior both derive
 * from this registry.
 *
 * Constraint: only applies when the API returns `response.data.codeError` from
 * `@novaheal/types` ERROR_CODES. Legacy endpoints (status + plain string body)
 * are invisible here — see frontend/docs/error-handling.md → Backend error contract.
 */
type CodeErrorConfig = {
  /** German user-facing copy for this code. */
  message: UserFacingError;
  /**
   * When true, the code is treated as an expected business outcome everywhere
   * (no Sentry report) without needing a per-query `expectedCodeErrors` opt-in.
   */
  globallyExpected?: boolean;
};

const FALLBACK_CODE_ERROR_MESSAGE: UserFacingError = {
  title: 'Hinweis',
  description: 'Die Anfrage konnte in der aktuellen Situation nicht ausgeführt werden.',
};

const CODE_ERROR_REGISTRY: Partial<Record<StatusCode, CodeErrorConfig>> = {
  [StatusCode.ADMIN_ACTION_NOT_ALLOWED_DURING_IMPERSONATION]: {
    globallyExpected: true,
    message: {
      title: 'Aktion nicht erlaubt',
      description: 'Diese Aktion ist während der Impersonation nicht möglich.',
    },
  },
  [StatusCode.ADMIN_PIN_INCORRECT]: {
    globallyExpected: true,
    message: {
      title: 'Falscher PIN',
      description: 'Der eingegebene PIN ist nicht korrekt. Bitte versuche es erneut.',
    },
  },
  [StatusCode.ADMIN_SESSION_STATUS_INVALIDATED]: {
    globallyExpected: true,
    message: {
      title: 'Session ungültig',
      description: 'Zu viele falsche Versuche. Die Session wurde ungültig gemacht.',
    },
  },
  [StatusCode.ADMIN_SESSION_TOKEN_INVALID]: {
    message: {
      title: 'Session abgelaufen',
      description: 'Die Admin-Session ist ungültig oder abgelaufen.',
    },
  },
  [StatusCode.ADMIN_SESSION_ACTIVATION_FORBIDDEN]: {
    message: {
      title: 'Aktivierung nicht erlaubt',
      description: 'Diese Admin-Session darf nicht aktiviert werden.',
    },
  },
  [StatusCode.ADMIN_REFRESH_TOKEN_DISABLED]: {
    message: {
      title: 'Anmeldung erforderlich',
      description: 'Bitte melde dich erneut an.',
    },
  },
};

function getCodeErrorConfig(codeError: string | undefined): CodeErrorConfig | undefined {
  if (!codeError) {
    return undefined;
  }

  return CODE_ERROR_REGISTRY[codeError as StatusCode];
}

export function getApiCodeError(error: unknown): string | undefined {
  if (!(error instanceof AxiosError)) {
    return undefined;
  }

  const codeError = (error.response?.data as { codeError?: string } | undefined)?.codeError;
  return typeof codeError === 'string' ? codeError : undefined;
}

function isExpectedCodeError(
  codeError: string | undefined,
  expectedCodeErrors?: string[]
): boolean {
  if (!codeError) {
    return false;
  }

  if (expectedCodeErrors?.includes(codeError)) {
    return true;
  }

  return getCodeErrorConfig(codeError)?.globallyExpected ?? false;
}

function resolveCodeErrorUserMessage(codeError: string): UserFacingError {
  return getCodeErrorConfig(codeError)?.message ?? FALLBACK_CODE_ERROR_MESSAGE;
}

function isSanctionedAccountError(error: AxiosError): boolean {
  const sanctionedMessageList = [
    'Your account is pending approval',
    'Your account has been rejected',
  ];

  return (
    error.response?.status === 403 &&
    sanctionedMessageList.includes((error.response?.data as { message: string })?.message)
  );
}

function classifyAxiosError(error: AxiosError, options?: ClassifyErrorOptions): ClassifiedError {
  const layer = options?.layer ?? 'fetch';
  const httpStatus = error.response?.status;
  const codeError = getApiCodeError(error);

  if (isSanctionedAccountError(error)) {
    return {
      layer,
      expectation: 'expected',
      userMessage: resolveAxiosUserMessage(httpStatus),
      codeError,
      httpStatus,
      shouldReport: false,
    };
  }

  if (codeError && isExpectedCodeError(codeError, options?.expectedCodeErrors)) {
    return {
      layer,
      expectation: 'expected',
      userMessage: resolveCodeErrorUserMessage(codeError),
      codeError,
      httpStatus,
      shouldReport: false,
    };
  }

  return {
    layer,
    expectation: 'unexpected',
    userMessage: resolveAxiosUserMessage(httpStatus),
    codeError,
    httpStatus,
    shouldReport: true,
  };
}

export function classifyError(error: unknown, options?: ClassifyErrorOptions): ClassifiedError {
  if (isTranslatorRelatedError(error)) {
    return {
      layer: 'runtime',
      expectation: 'expected',
      userMessage: resolveTranslatorUserMessage(),
      shouldReport: false,
    };
  }

  if (error instanceof AxiosError) {
    return classifyAxiosError(error, options);
  }

  return {
    layer: options?.layer ?? 'runtime',
    expectation: 'unexpected',
    userMessage: resolveRuntimeUserMessage(),
    shouldReport: true,
  };
}
